import { css, useTheme } from '@emotion/react'
import { useEffect, useState } from 'react'
import { observer, inject } from 'mobx-react'
import _ from 'lodash'
import dayjs from 'dayjs'

import { AiDocument, CircleNegative, CirclePlus, Link } from '../../../assets/icons'
import {
  RoundedCard,
  CardField,
  Money,
  SVGWrapper,
  HelpIcon,
  TooltipText,
} from '../../../components'
import { InvestmentChoice, InvestmentChoiceRow } from './AddInvestmentsStyle'
import { accountTypeKeyToPlanTypeId } from '../../../constants'
import { primeKeyboard, withRouterProps } from '../../../utils'
import { refreshPlaidLink, trackInvestmentsByTaxType } from '../utils'
import {
  Collapsible,
  AddInvestmentPill,
  EmptyStateActions,
  ExpanderRow,
  HeaderControls,
  InvestmentActions,
  InvestmentsCardBody,
  LinkedTimestamp,
  ProcessingRow,
  SummaryToggle,
  UpdateBalancesPill,
} from './EditInvestmentsCardStyle'
import useInvestmentsRefresh from './useInvestmentsRefresh'
import { UPLOAD_COPY, UploadStatementButton, useStatementUpload } from './upload'
import { SectionBalance } from './components/InvestmentRowStyle'
import InstitutionalInvestmentsDisplay from './components/InstitutionalInvestmentsDisplay'
import InvestmentsDisplay from './components/InvestmentsDisplay'
import LinkedAccountMenu from './components/LinkedAccountMenu'
import PlaidLink from './components/PlaidLink'

function WarningIcon(props) {
  return (
    <svg viewBox='0 0 24 24' width='1em' height='1em' {...props}>
      <title>warning</title>
      <g data-name='icn/warning' fill='#f48024'>
        <path d='M23.83 21.83L12.95.61a1 1 0 00-1.87 0L.15 21.92a1.31 1.31 0 000 1.22 1.17 1.17 0 00.93.86h21.86a1.12 1.12 0 001-.88 1.88 1.88 0 00-.11-1.29zm-20.52.22L12 4l9 18.07H3.31z' />
        <path
          data-name='!'
          d='M13 10.15h-2v4.14a10.67 10.67 0 00.09 1.29q.08.63.21 1.35h1.42q.13-.72.21-1.35a10.67 10.67 0 00.07-1.29v-4.14zm-2 8.94a1.1 1.1 0 00.07.43 1 1 0 00.21.33 1.08 1.08 0 00.33.23.91.91 0 00.4.09.9.9 0 00.38-.09 1 1 0 00.32-.23 1 1 0 00.21-.33 1.1 1.1 0 00.08-.44 1.15 1.15 0 00-.08-.43 1.11 1.11 0 00-.21-.34 1 1 0 00-.32-.23A.9.9 0 0012 18a.91.91 0 00-.4.09 1.08 1.08 0 00-.33.23 1.11 1.11 0 00-.21.34 1.15 1.15 0 00-.06.42z'
        />
      </g>
    </svg>
  )
}

const EditInvestmentsCard = props => {
  const {
    features,
    config,
    account: { managedAccount },
    plaidLinkedAccountIds,
    findPlaidAccountById,
  } = props.store
  const {
    account: { rothContribAllowed, posttaxContribAllowed },
    investments,
    investedInvestments,
    totalBalance,
    preTaxBalance,
    preTaxInvestments,
    rothBalance,
    rothInvestments,
    afterTaxBalance,
    afterTaxInvestments,
    availableInvestments,
  } = props.account
  const { type, accountType, id: accountId } = props.match.params
  const theme = useTheme()
  const [pendingAction, setPendingAction] = useState(null)
  const numericAccountId = Number(accountId)
  const { openUpload, isMobile } = useStatementUpload({
    store: props.store,
    type,
    accountType,
    accountId,
  })
  // A statement uploaded from anywhere in the app lands here, so this card has to pick up the
  // extracted investments even when it was already mounted.
  useInvestmentsRefresh({ store: props.store, account: props.account, type, accountId })
  const isProcessingStatement = props.store.isAccountUploading(accountId)
  const expandedView = props.account.expandedInvestmentsAccountId === numericAccountId
  const canEdit = type !== 'institutional' || features.editInstitutionalAccount
  const investmentsExist =
    investments.length > 0 || (type === 'institutional' && availableInvestments.length > 0)

  const showManagedAccountView =
    type === 'institutional' && features.managedAccountAvailable && managedAccount

  const toggleSummary = () => props.account.setInvestmentsExpanded(accountId, !expandedView)

  const refreshAccountData = () => {
    const { type, id } = props.match.params

    return Promise.all([
      props.store.getExistingPlaidLinks(),
      props.store.getAccounts(),
      props.account.getInvestments({ type, id }),
    ])
  }

  // Handle reload failures separately so a successful Plaid action is not reported as failed.
  const reloadAfterPlaidChange = async () => {
    try {
      await refreshAccountData()
    } catch (err) {
      console.error(err)
      props.store.showToast({
        severity: 'error',
        message: 'Your account was updated, but the new data could not be loaded.',
      })
    }
  }

  // Keep the menu busy until the follow-up reload finishes.
  const runPlaidAction = async (action, submit, failureMessage) => {
    if (pendingAction) return

    setPendingAction(action)

    try {
      await submit()
      await reloadAfterPlaidChange()
    } catch (err) {
      console.error(err)
      props.store.showToast({ severity: 'error', message: failureMessage })
    } finally {
      setPendingAction(null)
    }
  }

  const handlePlaidRefresh = () =>
    runPlaidAction(
      'refreshing',
      () => refreshPlaidLink(props.store, numericAccountId),
      'We could not refresh this account. Please try again.'
    )

  const handlePlaidUnlink = () =>
    runPlaidAction(
      'unlinking',
      () => props.store.unlinkPlaidAccount(numericAccountId),
      'We could not unlink this account. Please try again.'
    )

  useEffect(() => {
    const getAvailableInvestments = async () => await props.account.getAvailableInvestments()
    if (
      props.match.params.type === 'institutional' &&
      props.store.features.editInstitutionalAccount
    ) {
      getAvailableInvestments()
    }
  }, [props.account, props.match.params.type, props.store.features.editInstitutionalAccount])

  const managedAccountNote = showManagedAccountView && (
    <div
      css={css`
        margin-top: -12px;
        font-size: 12px;
        color: #7a8e96;
        margin-bottom: 16px;
      `}>
      {config.template.managedAccountLabel}
    </div>
  )

  const summaryToggleLabel = expandedView
    ? 'Hide investments'
    : canEdit
      ? 'View/update investments'
      : 'View investments'

  const summaryToggle = (
    <ExpanderRow $expanded={expandedView}>
      <SummaryToggle type='button' onClick={toggleSummary} aria-expanded={expandedView}>
        <SVGWrapper
          svg={expandedView ? CircleNegative : CirclePlus}
          fill={theme.lightestGray ?? '#7a8e96'}
          size='custom22'
        />
        {summaryToggleLabel}
      </SummaryToggle>
      {expandedView && investedInvestments.length > 0 && (
        <SectionBalance>
          Account balance <Money value={totalBalance} />
        </SectionBalance>
      )}
    </ExpanderRow>
  )

  /*
   * Mobile opens straight onto the security search with the keyboard up, which has to be raised from
   * inside this tap — the field it belongs to does not exist until the next screen mounts. See
   * primeKeyboard.
   */
  const handleAddInvestment = () => {
    if (isMobile) primeKeyboard()
    props.navigate(`/accounts/add/${type}/${accountType}/${accountId}/investment`)
  }

  const handleUpdateBalances = () =>
    props.navigate(`/accounts/${type}/${accountType}/${accountId}/update-balances`)

  const investmentActions = canEdit && (
    <InvestmentActions>
      <UpdateBalancesPill type='button' onClick={handleUpdateBalances}>
        Update balances only
      </UpdateBalancesPill>
      <AddInvestmentPill type='button' onClick={handleAddInvestment}>
        + Add investment
      </AddInvestmentPill>
    </InvestmentActions>
  )

  const plaidAccountLinked = _.includes(plaidLinkedAccountIds, numericAccountId)
  const plaidAccount = findPlaidAccountById(numericAccountId)
  const plaidAccountLastRefreshDate = _.get(plaidAccount, 'lastRefreshedOn', null)
  const requiresRelink = _.get(plaidAccount, 'requiresRelink', false)

  const plaidControlsAvailable =
    features.enablePlaidLink &&
    (type === 'non-gc' || (type === 'institutional' && features.editInstitutionalAccount))

  const showPlaidLinkControls = plaidControlsAvailable && plaidAccountLinked

  const emptyStateActions = canEdit && (
    <EmptyStateActions>
      <InvestmentChoiceRow>
        <InvestmentChoice
          label='Manually add'
          sublabel='manually add investments'
          variant='tertiaryLight'
          onClick={handleAddInvestment}
        />
        <InvestmentChoice
          label='Upload a statement'
          sublabel='let AI enter the numbers'
          onClick={openUpload}
        />
        {plaidControlsAvailable && !plaidAccountLinked && (
          <PlaidLink
            onSuccess={reloadAfterPlaidChange}
            accountId={accountId}
            bigButton
            planType={accountTypeKeyToPlanTypeId[accountType]}
          />
        )}
      </InvestmentChoiceRow>
    </EmptyStateActions>
  )

  const summaryBalances = (
    <div>
      <CardField
        isCurrency
        label='Account balance'
        subValue={
          investedInvestments.length === 1
            ? '(1 investment)'
            : `(${investedInvestments.length} investments)`
        }
        value={totalBalance}
      />

      {trackInvestmentsByTaxType(accountType) && (
        <div
          css={css`
            margin-top: 20px;
          `}>
          {preTaxBalance > 0 && (
            <CardField
              isCurrency
              label='Pre-tax balance'
              subValue={
                preTaxInvestments.length === 1
                  ? '(1 investment)'
                  : `(${preTaxInvestments.length} investments)`
              }
              value={preTaxBalance}
            />
          )}

          {rothBalance > 0 && (
            <CardField
              isCurrency
              label='Roth balance'
              subValue={
                rothInvestments.length === 1
                  ? '(1 investment)'
                  : `(${rothInvestments.length} investments)`
              }
              value={rothBalance}
            />
          )}

          {afterTaxBalance > 0 && (
            <CardField
              isCurrency
              label='After-tax balance'
              subValue={
                afterTaxInvestments.length === 1
                  ? '(1 investment)'
                  : `(${afterTaxInvestments.length} investments)`
              }
              value={afterTaxBalance}
            />
          )}
        </div>
      )}
    </div>
  )

  // Keep the toggle mounted below the collapsing summary so it slides into place as the summary closes.
  const investmentsView = investmentsExist && (
    <div>
      <Collapsible $open={!expandedView} aria-hidden={expandedView}>
        {summaryBalances}
      </Collapsible>
      {summaryToggle}
      {expandedView &&
        (type === 'institutional' ? (
          <InstitutionalInvestmentsDisplay />
        ) : (
          <>
            {investmentActions}
            <InvestmentsDisplay />
          </>
        ))}
    </div>
  )

  // The design replaces the old "no data on record" line with the three ways in, so the empty
  // state offers a next step instead of only reporting the gap.
  const noDataView = !investmentsExist && emptyStateActions

  // While a statement is being read, the card stands in for the whole summary: the numbers are
  // about to change, and the header controls would offer actions that conflict with the upload.
  const processingView = (
    <ProcessingRow>
      <AiDocument width='30' height='30' />
      {UPLOAD_COPY.cardProcessing}
    </ProcessingRow>
  )

  const linkedTimestamp = (
    <LinkedTimestamp>
      <Link />
      Linked • last refreshed{' '}
      {plaidAccountLastRefreshDate
        ? `${dayjs(plaidAccountLastRefreshDate).format('MM/DD/YYYY')} (${dayjs(
            plaidAccountLastRefreshDate
          ).fromNow()})`
        : 'Never'}
    </LinkedTimestamp>
  )

  const linkedMenu = (
    <LinkedAccountMenu
      requiresRelink={requiresRelink}
      accessId={plaidAccount?.accessId}
      getUpdateLinkToken={props.store.getUpdateLinkToken}
      onRefresh={handlePlaidRefresh}
      onUnlink={handlePlaidUnlink}
      onRelinkSuccess={reloadAfterPlaidChange}
      onRelinkError={() =>
        props.store.showToast({
          severity: 'error',
          message: 'We could not start relinking this account. Please try again.',
        })
      }
      pendingAction={pendingAction}
    />
  )

  const linkControl = plaidControlsAvailable && !plaidAccountLinked && (
    <PlaidLink
      onSuccess={reloadAfterPlaidChange}
      accountId={accountId}
      planType={accountTypeKeyToPlanTypeId[accountType]}
    />
  )

  // The empty state offers uploading and linking as full-size buttons, so the header would only
  // repeat them. Link status still belongs up there either way.
  const showHeaderEntryPoints = investmentsExist

  // Uploading replaces a statement's worth of investments, so it is only offered while the
  // account is not linked — a linked account refreshes itself. An account whose type cannot be
  // linked at all still gets the option, even if it appears in the linked ids.
  const uploadControl = canEdit && !showPlaidLinkControls && showHeaderEntryPoints && (
    <UploadStatementButton onClick={openUpload} />
  )

  // With linking available the two controls sit together beside the title; without it, upload
  // alone lines up with the Edit buttons on the account's other cards.
  const headerAside = showPlaidLinkControls
    ? linkedTimestamp
    : linkControl &&
      showHeaderEntryPoints && (
        <HeaderControls>
          {uploadControl}
          {linkControl}
        </HeaderControls>
      )

  const headerAction = showPlaidLinkControls ? linkedMenu : !linkControl && uploadControl

  const splitBalanceWarning = plaidControlsAvailable &&
    plaidAccountLinked &&
    trackInvestmentsByTaxType(accountType) &&
    (rothContribAllowed || posttaxContribAllowed) && (
      <div
        css={css`
          margin: 0 0 24px;
          color: #f48024;
          font-size: 14px;
          font-weight: 300;
        `}>
        <WarningIcon /> Edit investments to split balances between pre-tax and other tax types{' '}
        <HelpIcon size='medium' tooltip={TooltipText.plaidSplitBalances()} />
      </div>
    )

  if (isProcessingStatement) {
    return (
      <RoundedCard title='Investments'>
        <InvestmentsCardBody>{processingView}</InvestmentsCardBody>
      </RoundedCard>
    )
  }

  return (
    <RoundedCard title='Investments' headerAside={headerAside} headerAction={headerAction}>
      <InvestmentsCardBody>
        {splitBalanceWarning}
        {managedAccountNote}
        {noDataView}
        {investmentsView}
      </InvestmentsCardBody>
    </RoundedCard>
  )
}

export default withRouterProps(inject('store', 'account')(observer(EditInvestmentsCard)))
